import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import ClassSelector from '../../components/ClassSelector';
import useClassContext from '../../hooks/useClassContext';
import { teacherNavItems } from '../../constants/navigation';

const INSIGHT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'struggle_area', label: 'Struggle Areas', color: 'bg-red-100 text-red-700' },
  { value: 'strength_area', label: 'Strengths', color: 'bg-green-100 text-green-700' },
  { value: 'topic_trend', label: 'Topic Trends', color: 'bg-blue-100 text-blue-700' },
  { value: 'engagement_alert', label: 'Alerts', color: 'bg-yellow-100 text-yellow-700' },
];

const TYPE_COLORS = {
  struggle_area: { bg: 'bg-red-100', text: 'text-red-700', icon: '\uD83D\uDD34' },
  strength_area: { bg: 'bg-green-100', text: 'text-green-700', icon: '\uD83D\uDFE2' },
  topic_trend: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '\uD83D\uDCCA' },
  engagement_alert: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '\u26A0\uFE0F' },
};

/**
 * InsightsPage - Dedicated insights page for teachers
 */
const InsightsPage = () => {
  const { currentUser } = useAuth();
  const { selectedClassId, setSelectedClassId, appendClassFilter } = useClassContext();
  const [classSections, setClassSections] = useState([]);
  const [insightsData, setInsightsData] = useState(null);
  const [classSummary, setClassSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState(''); // '', 'true', 'false'
  const [page, setPage] = useState(1);

  // Bulk selection
  const [selectedInsights, setSelectedInsights] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Generate state
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  // Expanded insight IDs
  const [expandedInsights, setExpandedInsights] = useState(new Set());

  const fetchClassSections = useCallback(async () => {
    try {
      const dashData = await apiRequest(appendClassFilter('/api/teacher/dashboard'), 'GET');
      if (dashData.class_sections) setClassSections(dashData.class_sections);
      if (dashData.ai_insights_summary) setClassSummary(dashData.ai_insights_summary);
    } catch (err) {
      // Non-critical, summary will be empty
    }
  }, [appendClassFilter]);

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/api/teacher/insights/all?page=${page}&per_page=20`;
      if (selectedClassId) url += `&class_section_id=${selectedClassId}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (reviewedFilter) url += `&reviewed=${reviewedFilter}`;

      const data = await apiRequest(url, 'GET');
      setInsightsData(data);
      setSelectedInsights(new Set());
      setSelectAll(false);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, typeFilter, reviewedFilter, page]);

  useEffect(() => {
    fetchClassSections();
  }, [fetchClassSections]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedClassId, typeFilter, reviewedFilter]);

  // Handlers
  const toggleInsightSelection = (id) => {
    const next = new Set(selectedInsights);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedInsights(next);
    setSelectAll(false);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedInsights(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set((insightsData?.insights || []).map(i => i.id));
      setSelectedInsights(allIds);
      setSelectAll(true);
    }
  };

  const handleBulkReview = async () => {
    if (selectedInsights.size === 0) return;
    try {
      await apiRequest('/api/teacher/insights/bulk-review', 'PUT', {
        insight_ids: Array.from(selectedInsights)
      });
      fetchInsights();
    } catch (err) {
      alert(`Failed to mark as reviewed: ${err.message}`);
    }
  };

  const handleMarkReviewed = async (insightId) => {
    try {
      await apiRequest(`/api/teacher/insights/${insightId}/review`, 'POST');
      fetchInsights();
    } catch (err) {
      alert(`Failed to mark as reviewed: ${err.message}`);
    }
  };

  const handleGenerateForClass = async () => {
    try {
      setGenerating(true);
      setGenerateResult(null);

      const teacherId = currentUser?.id;
      const body = { timeframe: 'week', max_students: 10 };
      if (selectedClassId) body.class_section_id = selectedClassId;

      const result = await apiRequest(`/api/teacher/insights/class/${teacherId}/generate-batch`, 'POST', body);
      setGenerateResult(result);
      fetchInsights();
    } catch (err) {
      alert(`Failed to generate insights: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const toggleExpanded = (id) => {
    const next = new Set(expandedInsights);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedInsights(next);
  };

  const activeFilterCount = [typeFilter, reviewedFilter, selectedClassId].filter(Boolean).length;
  const typeCounts = insightsData?.type_counts || {};
  const pagination = insightsData?.pagination || {};

  return (
    <DashboardLayout title="AI Insights" navItems={teacherNavItems} userRole="teacher" userName={currentUser?.full_name || 'Teacher'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">AI Insights</h2>
        <button
          onClick={handleGenerateForClass}
          disabled={generating}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium text-sm"
        >
          {generating ? 'Generating...' : 'Generate for Class'}
        </button>
      </div>

      {/* Generate Result */}
      {generateResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="font-medium text-green-800">
            Generation complete: {generateResult.processed || 0} processed, {generateResult.skipped || 0} skipped, {generateResult.failed || 0} failed
          </p>
          <button onClick={() => setGenerateResult(null)} className="text-sm text-green-600 mt-1 hover:underline">Dismiss</button>
        </div>
      )}

      {/* Common Misconceptions Panel */}
      {classSummary?.common_struggles?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Common Misconceptions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {classSummary.common_struggles.slice(0, 5).map((s, i) => (
              <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <p className="font-medium text-red-800 text-sm">{s.topic}</p>
                <p className="text-xs text-red-600 mt-1">{s.student_count} student{s.student_count !== 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {classSections.length > 0 && (
            <ClassSelector
              classSections={classSections}
              selectedClassId={selectedClassId}
              onClassChange={(id) => { setSelectedClassId(id); }}
            />
          )}

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {INSIGHT_TYPES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label} {typeCounts[t.value] !== undefined ? `(${typeCounts[t.value]})` : ''}
              </option>
            ))}
          </select>

          <select
            value={reviewedFilter}
            onChange={(e) => setReviewedFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Status</option>
            <option value="false">Unreviewed ({typeCounts.unreviewed || 0})</option>
            <option value="true">Reviewed</option>
          </select>

          {activeFilterCount > 0 && (
            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
            </span>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {(insightsData?.insights || []).length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={toggleSelectAll}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Select all visible
          </label>
          {selectedInsights.size > 0 && (
            <button
              onClick={handleBulkReview}
              className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
            >
              Mark {selectedInsights.size} as reviewed
            </button>
          )}
        </div>
      )}

      {/* Insights Feed */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading insights...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error}
        </div>
      ) : (insightsData?.insights || []).length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-4xl mb-2">{'\uD83E\uDD16'}</p>
          <p className="text-gray-600 mb-2">No insights found</p>
          <p className="text-sm text-gray-500">Try adjusting your filters or generate new insights</p>
        </div>
      ) : (
        <div className="space-y-3">
          {insightsData.insights.map((insight) => {
            const typeInfo = TYPE_COLORS[insight.insight_type] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: '\uD83D\uDCA1' };
            const isExpanded = expandedInsights.has(insight.id);

            return (
              <div
                key={insight.id}
                className={`bg-white rounded-lg border p-4 transition-colors ${
                  insight.reviewed_by_teacher ? 'border-gray-200' : 'border-indigo-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedInsights.has(insight.id)}
                    onChange={() => toggleInsightSelection(insight.id)}
                    className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeInfo.bg} ${typeInfo.text}`}>
                        {typeInfo.icon} {insight.insight_type.replace('_', ' ')}
                      </span>
                      <span className="text-sm font-medium text-gray-700">{insight.student_name}</span>
                      {insight.confidence_score && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {Math.round(insight.confidence_score * 100)}% confidence
                        </span>
                      )}
                      {insight.reviewed_by_teacher ? (
                        <span className="text-xs text-green-600 font-medium">Reviewed</span>
                      ) : (
                        <span className="text-xs text-indigo-600 font-medium">New</span>
                      )}
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">{insight.topic}</h4>
                    <p className="text-sm text-gray-600 mt-1">{insight.summary}</p>

                    {/* Expandable detail */}
                    {insight.detailed_analysis && (
                      <div className="mt-2">
                        <button
                          onClick={() => toggleExpanded(insight.id)}
                          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          {isExpanded ? 'Hide details' : 'View details'}
                        </button>
                        {isExpanded && (
                          <p className="mt-2 text-sm text-gray-600 pl-3 border-l-2 border-gray-300">
                            {insight.detailed_analysis}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-500">
                        {insight.insight_date ? new Date(insight.insight_date).toLocaleDateString() : ''}
                      </span>
                      {!insight.reviewed_by_teacher && (
                        <button
                          onClick={() => handleMarkReviewed(insight.id)}
                          className="text-xs px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                        >
                          Mark as reviewed
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={!pagination.has_prev}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!pagination.has_next}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </DashboardLayout>
  );
};

export default InsightsPage;
